import React, { FC } from 'react'
import styles from './styles.module.scss'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import { ColourLegend } from 'components/colourLegend'
import { useTransition, animated as a, config } from 'react-spring'

interface Part {
  readonly amount: number
  readonly color: string
  readonly label: string
}

export interface Props {
  hideLegend?: boolean
  locale: 'de' | 'fr'
  parts: Part[]
  showMidpoint: boolean
  title?: string
}

/**
 * Shows relations in a strip with different colors and an
 * indication where 50% is.
 */
export const PercentageStrip: FC<Props> = ({
  parts,
  locale,
  title,
  hideLegend = false,
  showMidpoint = false
}) => {
  /**
   * Legend won't be displayed for values lower than this.
   * @type {number}
   * @private
   */
  const legendCutOff = 0.02

  const formatPercent = new Intl.NumberFormat(`${locale}-CH`, {
    style: 'percent'
  })
  const formatCSSPercent = (fraction: number) => `${fraction * 100}%`

  const onlyBadData = parts.every(
    value => isNaN(value.amount) || value.amount === 0
  )

  /** Calculate the total amount of all parts together */
  const totalAmount = parts.reduce<number>(
    (previousValue, currentValue) => previousValue + currentValue.amount,
    0
  )

  const transitions = useTransition(
    parts.map((part, index, array) => ({
      ...part,
      x:
        array
          .slice(0, index)
          .reduce<number>(
            (previousValue, currentValue) =>
              previousValue + (currentValue.amount / totalAmount) * 100,
            0
          ) || 0,
      width: (part.amount / totalAmount) * 100 || 0,
      color: part.color,
      left: formatCSSPercent(
        array
          .slice(0, index)
          .reduce<number>(
            (previousValue, currentValue) =>
              previousValue + currentValue.amount / totalAmount,
            0
          ) || 0
      ),
      right: formatCSSPercent(
        index + 1 < array.length
          ? array
              .slice(index + 1 - array.length)
              .reduce<number>(
                (previousValue, currentValue) =>
                  previousValue + currentValue.amount / totalAmount,
                0
              ) || 0
          : 0
      )
    })),
    d => d.label,
    {
      enter: ({ x, width, left, right }) => ({
        x,
        width,
        left,
        right
      }),
      update: ({ x, width, left, right }) => ({
        x,
        width,
        left,
        right
      }),
      unique: true,
      config: config.gentle
    }
  )

  const strip = (
    <>
      <svg
        className={styles.strip}
        viewBox='0 0 100 10'
        preserveAspectRatio='none'
      >
        {transitions.map(({ item, props: { x, width }, key }) => (
          <a.rect
            key={key}
            x={x}
            y={0}
            width={width.interpolate({
              extrapolateLeft: 'clamp',
              output: [0, 100]
            })}
            height='100%'
            fill={item.color}
          />
        ))}
      </svg>

      <div className={styles.legendStrip}>
        {transitions.map(({ item, props, key }) => {
          if (item.amount / totalAmount > legendCutOff) {
            return (
              <a.div
                key={key}
                className={styles.percentage}
                style={{ left: props.left, right: props.right }}
              >
                {formatPercent.format(item.amount / totalAmount)}
              </a.div>
            )
          }
        })}
      </div>

      <div className={styles.legend}>
        {showMidpoint && (
          <>
            <div className={styles.pointer} role='' />
            <div className='label'>50%</div>
          </>
        )}
      </div>
    </>
  )

  return (
    <figure className={styles.percentageStrip}>
      {title && <figcaption className={styles.caption}>{title}</figcaption>}
      {!hideLegend && <ColourLegend parts={parts} />}
      {onlyBadData && (
        <p className={styles.noData}>
          <i className='times circle icon' /> Keine Daten vorhanden
        </p>
      )}
      {!onlyBadData && strip}
    </figure>
  )
}
